import React from 'react';
import { Box, Card, CardContent, Typography, Stack, Avatar, Divider, Tooltip } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatMoney } from "../../producer-dashboard/ProducerDashboardHelper";
import { FlowRow } from './FlowRow';

type EscrowWalletCardProps = {
    selectedTab: string;
    currency: string;
    getVal: (mapName: string, op: string) => number;
    getDetails: (mapName: string, op: string) => any;
};

export const EscrowWalletCard = ({ selectedTab, currency, getVal, getDetails }: EscrowWalletCardProps) => {
    return (
        <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 4, height: '100%' }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', mb: 0.5 }}>
                            Escrow Wallet Details
                        </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: '#f0fdf4', color: '#16a34a', width: 32, height: 32 }}>
                        <AccountBalanceIcon fontSize="small" />
                    </Avatar>
                </Stack>

                <Box sx={{ flexGrow: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} mb={1}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', letterSpacing: '0.5px' }}>EXPECTED CREDITS</Typography>
                        <Tooltip title="Automated refills are only active for EUR and PLN. For other currencies, this amount will be zero." arrow placement="top">
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'pointer' }} />
                        </Tooltip>
                    </Stack>

                    <FlowRow
                        label="Awaited Refill"
                        amount={getVal('amount_awaited_to_refill', selectedTab)} type="in" currency={currency}
                        tooltip="Refills waiting to be requested. Includes transactions created before today at midnight that have not yet been integrated into the hourly refill batch."
                    />
                    <FlowRow
                        label="Requested Refill"
                        amount={getVal('amount_awaited_requested_to_refill', selectedTab)} type="in" currency={currency}
                        tooltip="Refills requested from the bank, pending wire transfer. To be refilled, transactions from Mirakl and OnePay must be successfully reconciled."
                    />

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', letterSpacing: '0.5px', mb: 1, display: 'block' }}>EXPECTED DEBITS</Typography>
                    <FlowRow
                        label="Transfers to Producers"
                        amount={getVal('amount_awaited_transfer_from_escrow_to_all_producers', selectedTab)}
                        details={getDetails('amount_awaited_transfer_from_escrow_to_all_producers', selectedTab)}
                        type="out" currency={currency}
                        tooltip="Represents all pending transfers to Producer wallets (PAYMENT, REFUND COD, CREDIT NOTE)."
                    />
                    <FlowRow
                        label="Transfers to SC"
                        amount={getVal('amount_awaited_transfer_from_escrow_to_operator', selectedTab)}
                        details={getDetails('amount_awaited_transfer_from_escrow_to_operator', selectedTab)}
                        type="out" currency={currency}
                        tooltip="Represents all pending transfers to the SC (PAYMENT)."
                    />
                </Box>

                {getVal('escrow_balance_by_operator', selectedTab) !== -1 && (
                    <Box sx={{ mt: 4, pt: 2, borderTop: '1px dashed #cbd5e1' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Projected Escrow Balance</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                {formatMoney(
                                    getVal('escrow_balance_by_operator', selectedTab)
                                    + getVal('amount_awaited_to_refill', selectedTab)
                                    + getVal('amount_awaited_requested_to_refill', selectedTab)
                                    - getVal('amount_awaited_transfer_from_escrow_to_all_producers', selectedTab)
                                    - getVal('amount_awaited_transfer_from_escrow_to_operator', selectedTab),
                                    currency
                                )}
                            </Typography>
                        </Stack>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};